import React, { useMemo, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { varClass } from '../../shared'
import { IRejectionReviewTrendRow } from '../../shared/types'
import { RejectionMaturity } from '../RejectionReview'
import { formatDecimal, formatDkkLarge, formatInteger } from '../shared'

// Sense-of-scale chart for the monthly rejection review. Mirrors the
// TimelineChart on daily triage in spirit: a single horizontal context bar
// that anchors the operator before they dive into the table.
//
// Receives pre-aggregated per-month rows from the backend's /trend endpoint.
// The endpoint also returns the range-level totals (used by the summary
// cards) so the whole page boots on a single fetch.

// Rejection-family blue (matches triage's FAMILY_COLORS.rejection) so the
// monthly trend visually belongs to the same product as the triage page.
// Grey stays for "open / still cooling" — same affordance as before.
const MATURITY_COLORS: Record<RejectionMaturity, string> = {
  completed: '#4F9EEC',
  open: '#828B9E',
}

type Metric = 'sale' | 'conversions'

const METRIC_LABELS: Record<Metric, string> = {
  sale: 'Rejected sale (DKK)',
  conversions: 'Rejected conv. (count)',
}

// Compact Y-axis tick formatter — "1.2M" / "350K" / "500". Keeps the axis
// narrow so the chart's plot area gets the room.
const compactNumber = (v: number): string => {
  if (v == null || isNaN(v)) return ''
  const abs = Math.abs(v)
  if (abs >= 1_000_000) return `${formatDecimal(v / 1_000_000, 1).replace(/,0$/, '')}M`
  if (abs >= 1_000) return `${formatDecimal(v / 1_000, 0)}K`
  return formatDecimal(Math.round(v), 0)
}

const formatMonthLabel = (ym: string): string => {
  try {
    const iso = ym.length === 10 ? ym : `${ym}-01`
    return format(parseISO(iso), 'MMM yy')
  } catch {
    return ym
  }
}

const currentYM = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// Month-level maturity. Each bar in the trend chart represents exactly one
// month, so the maturity is a single flag (not a per-row split). A month is
// "open" only if it's the current calendar month or in the future —
// everything else has ended and is "completed".
const monthMaturity = (ym: string): RejectionMaturity =>
  ym.slice(0, 7) >= currentYM() ? 'open' : 'completed'

type Bucket = {
  month: string
  monthLabel: string
  total: number
  maturity: RejectionMaturity
}

export const RejectionTrendChart = (props: {
  rows: IRejectionReviewTrendRow[] | null
  selectedMonth?: string // 'YYYY-MM' when the date range is a single month
  onSelectMonth?: (month: string) => void
}) => {
  const { rows, selectedMonth, onSelectMonth } = props
  const [metric, setMetric] = useState<Metric>('sale')

  const chartData: Bucket[] = useMemo(() => {
    if (!rows) return []
    return rows.map((r) => {
      const month = r.conversionMonth.slice(0, 7)
      const total =
        metric === 'sale'
          ? r.totalRejectedSaleDkk ?? 0
          : r.totalRejectedConversions ?? 0
      return {
        month,
        monthLabel: formatMonthLabel(month),
        total,
        maturity: monthMaturity(r.conversionMonth),
      }
    })
  }, [rows, metric])

  const loading = rows == null

  const handleClick = (entry: any) => {
    if (!onSelectMonth) return
    const month = entry?.activeLabel
    if (typeof month === 'string') onSelectMonth(month)
  }

  return (
    <div className="card anomalies-trend-chart">
      <div className="card-body p-y-20">
        <div className="row row-space-between row-center m-b-3">
          <div>
            <div className="text-dark text-bolder">Rejection trend</div>
            <div className="text-light text-small m-t-05">
              Monthly rejection volume across the selected range. Click a bar
              to focus that month.
            </div>
          </div>
          <div className="anomalies-rejection-review-view-toggle">
            {(Object.keys(METRIC_LABELS) as Metric[]).map((m) => (
              <button
                key={m}
                type="button"
                className={varClass({
                  'anomalies-rejection-review-view-toggle-btn': true,
                  active: metric === m,
                })}
                onClick={() => setMetric(m)}
              >
                {METRIC_LABELS[m]}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center text-light p-y-10">Loading trend…</div>
        ) : chartData.length === 0 ? (
          <div className="text-center text-light p-y-10">
            No months in this range.
          </div>
        ) : (
          <ResponsiveContainer width="99.9%" height={180}>
            <ComposedChart
              data={chartData}
              margin={{ top: 8, left: 0, right: 0, bottom: 0 }}
              onClick={handleClick}
            >
              <CartesianGrid
                horizontal
                vertical={false}
                strokeDasharray="6"
                stroke="#EDEFF2"
              />
              {selectedMonth && (
                <ReferenceLine
                  x={selectedMonth}
                  stroke="rgba(79, 158, 236, 0.7)"
                  strokeWidth={2}
                />
              )}
              <Tooltip content={<TrendTooltip metric={metric} />} />
              <Bar
                dataKey="total"
                cursor="pointer"
                isAnimationActive={false}
                radius={[3, 3, 0, 0]}
              >
                {chartData.map((d) => (
                  <Cell key={d.month} fill={MATURITY_COLORS[d.maturity]} />
                ))}
              </Bar>
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                tickFormatter={formatMonthLabel}
                tick={{ fontSize: 11, fill: '#828B9E' }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={44}
                tickFormatter={(v) => compactNumber(v)}
                tick={{ fontSize: 11, fill: '#828B9E' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

const TrendTooltip = (props: any) => {
  const { active, payload, label, metric } = props
  if (!active || !payload || !payload.length) return null
  const row = payload[0]?.payload as Bucket | undefined
  if (!row) return null

  const fmt = (n: number) =>
    metric === 'sale' ? formatDkkLarge(n) : formatInteger(n)

  return (
    <div className="anomalies-chart-tooltip">
      <div className="anomalies-chart-tooltip-date">
        {format(parseISO(`${label}-01`), 'MMMM yyyy')}
      </div>
      <Row label="Total" value={fmt(row.total)} color="#1F2937" />
    </div>
  )
}

const Row = (props: { label: string; value: string; color: string }) => (
  <div className="anomalies-chart-tooltip-row">
    <span
      className="anomalies-chart-tooltip-dot"
      style={{ background: props.color }}
    />
    <span className="anomalies-chart-tooltip-label">{props.label}</span>
    <span className="anomalies-chart-tooltip-value">{props.value}</span>
  </div>
)
